import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { MapPin, Star } from 'lucide-react';
import { featureFlags } from '@/utils/featureFlags';
import { MobileNavItem } from './MobileNavItem';
import { MobileNavSection } from './MobileNavSection';
import { MobileNavAuthSection } from './MobileNavAuthSection';
import { primaryNavItems, adminNavItems, externalLinks } from './mobileNavConfig';

export interface MobileNavDrawerContentProps {
  isAuthenticated: boolean;
  isPremium: boolean;
  isAdmin: boolean;
  currentState: string;
  user?: { name?: string; email?: string; avatar?: string };
  trackedCount: number;
  currentPath: string;
  onNavigate: (to: string, external?: boolean) => void;
  onSignOut: () => void;
  onAuthAction: () => void;
  onStartTour: () => void;
}

export const MobileNavDrawerContent: React.FC<MobileNavDrawerContentProps> = ({
  isAuthenticated,
  isPremium,
  isAdmin,
  currentState,
  user,
  trackedCount,
  currentPath,
  onNavigate,
  onSignOut,
  onAuthAction,
  onStartTour,
}) => {
  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* State Selector - gated by feature flag */}
      {featureFlags.stateSelector && (
        <div className="px-6 py-4 bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4 text-helper" />
              <span className="text-sm font-medium text-foreground">{currentState}</span>
            </div>
            <Badge
              variant="outline"
              className="bg-secondary-50 dark:bg-secondary-950 text-secondary-700 dark:text-secondary-300 border-secondary-200 dark:border-secondary-800 text-xs"
            >
              More states coming
            </Badge>
          </div>
        </div>
      )}

      {/* Primary Navigation */}
      <MobileNavSection ariaLabel="Primary navigation">
        <div className="space-y-1">
          {primaryNavItems.map((item) => (
            <MobileNavItem
              key={item.to}
              item={item}
              isActive={currentPath === item.to}
              onClick={() => onNavigate(item.to)}
            />
          ))}
        </div>
      </MobileNavSection>

      {/* Dashboard - Only visible to authenticated users */}
      {isAuthenticated && (
        <MobileNavSection showSeparator>
          <MobileNavItem
            item={{
              label: 'Dashboard',
              to: '/dashboard',
              icon: <Star className="w-5 h-5" fill={trackedCount > 0 ? 'currentColor' : 'none'} />,
              description: "Bills and committees you're tracking",
            }}
            isActive={currentPath === '/dashboard'}
            onClick={() => onNavigate('/dashboard')}
            colorScheme="amber"
            badge={
              trackedCount > 0 ? (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-xs font-semibold text-white">
                  {trackedCount > 99 ? '99+' : trackedCount}
                </span>
              ) : undefined
            }
          />
        </MobileNavSection>
      )}

      {/* Admin Navigation - Only visible to authenticated admin users */}
      {isAuthenticated && isAdmin && (
        <MobileNavSection heading="Admin" showSeparator>
          <div className="space-y-1">
            {adminNavItems.map((item) => (
              <MobileNavItem
                key={item.to}
                item={item}
                isActive={currentPath === item.to}
                onClick={() => onNavigate(item.to)}
              />
            ))}
          </div>
        </MobileNavSection>
      )}

      <Separator />

      {/* External Links */}
      <MobileNavSection heading="External Resources">
        <div className="space-y-1">
          {externalLinks.map((item) => (
            <MobileNavItem
              key={item.to}
              item={item}
              isActive={false}
              onClick={() => onNavigate(item.to, item.external)}
            />
          ))}
        </div>
      </MobileNavSection>

      <Separator />

      {/* Authentication Section */}
      <div className="px-6 py-4 mt-auto">
        <MobileNavAuthSection
          isAuthenticated={isAuthenticated}
          isPremium={isPremium}
          user={user}
          onNavigate={onNavigate}
          onSignOut={onSignOut}
          onAuthAction={onAuthAction}
          onStartTour={onStartTour}
        />
      </div>
    </div>
  );
};
